import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { useEmployeeDashboard } from "hooks/useEmployeeDashboard";
import {
  GreetingHeader,
  ActivateCardBanner,
  QuickActions,
  ComplianceStats,
  MyCardsPanel,
  OutstandingRequestsPanel,
} from "components/employee/dashboard";
import RouterLinkBehavior from "components/common/RouterLinkBehavior";
import RecentTransactions from "components/common/transactions/RecentTransactions";
import {
  CARD_SPEND_STATUSES,
  CARD_TYPES,
  PERSONAL_SPEND_STATUSES,
} from "utils/transactions/transactionGroups";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import { useTranslation } from "react-i18next";
import ViolationType from "enums/ViolationType";
import { myTransactionsLink } from "utils/transactions/myTransactionsLink";
import UploadReceiptDrawer from "components/common/receiptLibrary/uploadReceipt/UploadReceiptDrawer";
import CardFormDrawer from "components/common/cards/CardFormDrawer";
import ReimbursementDrawer from "components/common/reimbursements/ReimbursementDrawer";
import { Columns, Stack } from "components/layout/reusableLayouts";
import useShowReimbursements from "hooks/useShowReimbursements";
import { Card, PageContainer } from "@outpave/ds";

/**
 * Employee Dashboard (/my/dashboard).
 *
 * This page is composition-only. All data fetching and GraphQL → view-model
 * mapping lives in `useEmployeeDashboard`; this file just plumbs the
 * returned values into the section components.
 */
const EmployeeDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [detailTransactionId, setDetailTransactionId] = useState<string | null>(
    null,
  );
  const { user } = useGetLoggedInUser();
  const navigate = useNavigate();
  const {
    totalTasks,
    myCards,
    issuedCards,
    errors,
    loading,
    missingReceiptsCount,
    missingAllocationsCount,
    outstandingRequests,
  } = useEmployeeDashboard();

  // Quick-action drawers are owned here (composition-only page) and opened
  // from QuickActions, mirroring how ReceiptLibrary controls its upload drawer.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [requestCardOpen, setRequestCardOpen] = useState(false);
  const [submitReimbursementOpen, setSubmitReimbursementOpen] = useState(false);

  // The same gate the admin surfaces use. Without it an employee on a tenant
  // without the feature could still file a reimbursement no admin can see or pay.
  const showReimbursements = useShowReimbursements();

  const goToTasks = (compliance: ViolationType) =>
    navigate(myTransactionsLink({ tab: "task", compliance }));

  return (
    <PageContainer wrapped={false}>
      <Stack gap="var(--space-7)">
        <ActivateCardBanner
          issuedCards={issuedCards}
          hasError={errors.cards}
          isLoading={loading.cards}
        />
        <GreetingHeader firstName={user?.firstName} tasksDue={totalTasks} />
        <QuickActions
          onUploadReceipt={() => setUploadOpen(true)}
          onRequestCard={() => setRequestCardOpen(true)}
          onSubmitReimbursement={
            showReimbursements
              ? () => setSubmitReimbursementOpen(true)
              : undefined
          }
          onActivateCard={
            issuedCards.length > 0 ? () => navigate("/my/activate") : undefined
          }
          onResolveCompliance={() => goToTasks(ViolationType.MISSING_RECEIPT)}
        />
        <ComplianceStats
          missingReceipts={missingReceiptsCount}
          missingAllocations={missingAllocationsCount}
          loadingReceipts={loading.receipts}
          loadingAllocations={loading.allocations}
          onSelectReceipts={() => goToTasks(ViolationType.MISSING_RECEIPT)}
          onSelectAllocations={() =>
            goToTasks(ViolationType.MISSING_COST_ALLOCATION)
          }
        />
        <Columns count={2} gap="var(--space-7)">
          <Card
            variant="outlined"
            title={t("Recent transactions")}
            subtitle={t("Your recent card activity")}
            action={{
              type: "link",
              href: "/my/transactions",
              label: t("View all transactions"),
              component: RouterLinkBehavior,
            }}
            sx={{ flex: 1 }}
          >
            <RecentTransactions
              scope="mine"
              statuses={[...CARD_SPEND_STATUSES, ...PERSONAL_SPEND_STATUSES]}
              types={CARD_TYPES}
              onRowClick={setDetailTransactionId}
            />
          </Card>
          <Stack gap="var(--space-7)">
            <MyCardsPanel cards={myCards} maxItems={3} />
            <OutstandingRequestsPanel
              reimbursements={outstandingRequests.reimbursements}
              personalExpenses={outstandingRequests.personalExpenses}
              showReimbursements={Boolean(showReimbursements)}
            />
            <TransactionDetailsDrawer
              open={!!detailTransactionId}
              transactionId={detailTransactionId ?? ""}
              handleCloseDrawer={() => setDetailTransactionId(null)}
            />
          </Stack>
        </Columns>

        <UploadReceiptDrawer
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
        />
        <CardFormDrawer
          open={requestCardOpen}
          onClose={() => setRequestCardOpen(false)}
        />
        {showReimbursements && (
          <ReimbursementDrawer
            open={submitReimbursementOpen}
            onClose={() => setSubmitReimbursementOpen(false)}
          />
        )}
      </Stack>
    </PageContainer>
  );
};

export default EmployeeDashboard;
